import { ChangeEvent, ReactNode, useContext, useEffect, useState } from 'react'
import { Box, Button, CircularProgress, FormControlLabel, IconButton, InputLabel, MenuItem, Select, SelectChangeEvent, Stack, Switch, TextField, Typography } from '@mui/material'
import { IShoppingCartPaymentRequest, ShoppingCartPaymentService } from '../services/shopping-cart.service'
import { CPVStoreOrderSuccessComponent } from '../order-success'
import { ICart } from '../helpers/cart.helper'
import { usd } from '../helpers/storefront.helper'
import { useRegionsHook } from '../../../hooks/regions.hook'
import { empty } from '../../../helpers/util.helper'
import { BootStrapSpinner } from '../../ui/views/spinner'
import { IAnyType } from '../../../interfaces/form-builder.interface'
import { environment } from '../../../environmentals/config'
import { HelioCheckout, HelioEmbedConfig, NetworkType } from '@heliofi/checkout-react'
import IComponentState, {
    ComponentStateDto,
    ComponentStateLoadingDto,
    ComponentStateReadyDto
} from '../../../interfaces/component-state.interface'
import { GeneralAccountSettingsGetService, ISettingsUser } from '../../../services/settings'
import { AccountContext } from '../../../providers/account.provider'
import ViewMainWindow, { viewMainWindowGreyBackgroundPreset } from '../../ui/views/main-window'
import UploadCsvAlert from '../../upload/alert'
import AlertBox from '../../upload/alert'
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import useOrderSummaryHook from '../../../hooks/order-summary.hook'
import 'https://js.globalpay.com/v1/globalpayments.js'
import './styles.scss'


const requiredFields = [
    'address_1',
    'region',
    'postcode',
    'city'
]

export interface IPaymentResponse
{
    cardBin: number
    cardLast4: number
    cardNumber: string
    cardSecurityCode: boolean
    cardType: string
    cardholderName: string
    expiryMonth: number
    expiryYear: number
}

export interface ISetOrderSuccessComponent
{
    summary: any,
    ready: boolean
}

export const HeartlandIFrameComponent = ({ cart, onSubmitSuccessEvent, onCancelEvent }: ICart) => {
    const [ orderSuccess, setOrderSuccess ] = useState<ISetOrderSuccessComponent>({summary: {}, ready: false})
    const [ data, setData ] = useState({order_type: cart.order_type || 'subscription'}) 
    const [ errors, setErrors ] = useState<string[]>([])
    const [ msg, setMsg ] = useState({msg: '', type: false})
    const [ state, setState ] = useState<IComponentState>(ComponentStateDto)
    const [ buttonState, setButtonState ] = useState<IComponentState>(ComponentStateDto)
    const [ paymentState, setPaymentState ] = useState<IComponentState>(ComponentStateDto)
    const { regions } = useRegionsHook();
    const [ paymentMethods, setPaymentMethods ] = useState<('crypto' | 'fiat')[]>([]);

    const { setPaymentMethod, order, paymentMethod, orderState, refresh } = useOrderSummaryHook(cart?.orderRequest || cart || {}, true);

    const onFreeProductSubmit = () => {
        const f: any = {...data, ...cart};
        f.order_type = 'freeherd';
        if(!buttonState.loading) {
            setButtonState(ComponentStateLoadingDto);
            ShoppingCartPaymentService(f).then((r: any) => {
                if(r.success) {
                    if(onSubmitSuccessEvent)
                        onSubmitSuccessEvent({ summary: r.data, ready: true })
                    else
                        setOrderSuccess({ summary: r.data, ready: true })
                } else {
                    setMsg({msg: r.error, type: false})
                }
                setButtonState(ComponentStateDto)
            });
        }
    }

    const cardForm = (error: any, success: any, GlobalPayments: any) => {
        const f = GlobalPayments.creditCard.form("#credit-card")
        f.on("token-success", (resp: any) => {
            // add payment token to form as a hidden input
            const token = document.createElement("input")
            token.type = "hidden"
            token.name = "payment_token"
            token.value = resp.paymentReference
            const form: any = document.getElementById("payment-form")
            form.appendChild(token)
            success(new FormData(form), resp.details)
        })
    
        f.on("token-error", (resp: any) => {
            error(resp)
        })
    }

    const error = (e: { error: boolean, reasons: {code: number, message: string }[] } ) => {
        const errors: string[] = []
        e.reasons.map((v) => {
            errors.push(v.message)
        })
        setMsg({type: false, msg: errors.join("\r\n")})
    }
    
    const success = (form: FormData, details: IPaymentResponse) => {
        setErrors([])
        setMsg({msg: '', type: false})
        if(!state.loading) {
            setState(ComponentStateLoadingDto)
            let allow: boolean = true
            const f: any | IShoppingCartPaymentRequest = { ...cart, ...details }

            form.forEach((v, k: any) => {
                if(requiredFields.includes(k) && v === '') {
                    allow = false
                    setErrors(arr => [...arr, k])
                } else if(k === 'region' && v === 'Select') {
                    setErrors(arr => [...arr, k])
                    allow = false
                }
                f[k] = v
            })

            if(!f.cardholderName || empty(f.cardholderName.trim())) {
                allow = false
            }

            if(!allow) {
                setMsg({msg: 'You are missing required fields.', type: false})
                setState(ComponentStateDto)
            } else {
                // Run the payment action
                ShoppingCartPaymentService(f).then((r: any) => {
                    if(r.success) {
                        if(onSubmitSuccessEvent)
                            onSubmitSuccessEvent({ summary: r.data, ready: true })
                        else
                            setOrderSuccess({ summary: r.data, ready: true })
                    } else {
                        setMsg({msg: r.error, type: false})
                    }
                    setState(ComponentStateDto)
                })
            }
        }
    }

    useEffect(() => {
        if(!paymentState.ready) {
            const form: HTMLElement | null = document.getElementById("credit-card")
            if(form?.childElementCount === 0) {
                // @ts-ignore
                if(GlobalPayments) {
                    // @ts-ignore
                    GlobalPayments.configure({
                        publicApiKey: environment.heartlandPublicKey,
                    })
                    // @ts-ignore
                    cardForm(error, success, GlobalPayments)
                    setPaymentState(ComponentStateReadyDto)
                }
            }
        }
    }, [])

    const onChangeEvent = (e: any, transformer?: any) => {
        const { name, value } = e.target
        setData((arr: any) => ({...arr, [name]: typeof transformer === "function"? transformer(value) : value }))
    }

    const getSystemSettings = async () => {
        const r = await GeneralAccountSettingsGetService(cart?.orderRequest?.uid || '');
        if(r.success) {
            setPaymentMethods(r.data?.global?.paymentTypes || []);
        }
    }

    useEffect(() => {
        getSystemSettings();
    }, []);

    return (
        orderSuccess.ready? 
        <ViewMainWindow
            hideLogo
        >
            <CPVStoreOrderSuccessComponent {...orderSuccess} />
        </ViewMainWindow>
        :
        <ViewMainWindow
            {...{...viewMainWindowGreyBackgroundPreset, wrapperClassName: `${viewMainWindowGreyBackgroundPreset.wrapperClassName} cart-payment`}}
        >
            <div className="col-count-3 gapped-2 col-c1-lg" id='cart-onetime-payment' style={{minHeight: cart.total <= 0? '100vh' : '0', position: cart.total <= 0? 'fixed' : 'relative', top: cart.total <= 0? '0' : 'auto', left: cart.total <= 0? '0' : 'auto', right: cart.total <= 0? '0' : 'auto', bottom: cart.total <= 0? '0' : 'auto', overflowY: cart.total <= 0? 'auto' : 'hidden'}}>
                <div className='span2 span1-lg'>
                    <h4 className='main-header mb-4 uppercase'>Cart Summary</h4>
                    { orderState.ready && (
                    <div className='shadow p-3 bg-white rounded border' id="cart-summary-item-list">
                        { cart.item_codes && cart.item_codes.map((v, k) => (
                        <div key={`cart-item-${k}`} className="cart-summary-wrapper">
                            <div className="cart-thumbnail">
                                <img src={ v.image } alt={`${v.title} Image`} />
                            </div>
                            <div className="cart-body d-flex align-items-center">
                                <div>
                                    <Typography className='m-0' variant='h6'>{ v.title }</Typography>
                                    { typeof v.description === "string"? <p className='m-0' dangerouslySetInnerHTML={{__html: v.description }} ></p> :  v.description }
                                </div>
                            </div>
                            <div className="cart-subtotal col-count-3 gapped">
                            <span></span><span><b>{v.qty}QTY</b></span><span><b>{ usd(v.subtotal) }</b></span>
                            </div>
                        </div>
                        )) }
                        <div className="cart-summary-wrapper cart-summary-totals">
                            <div className='col-count-2 col-c1-lg'>
                                <div></div>
                                <div>
                                    <div className='cart-subtotal'>
                                        <div>Subtotal</div>
                                        <div>{ usd(cart.subtotal) }</div>
                                    </div>
                                    { cart?.fees? 
                                    (<div className='cart-fees'>
                                        <div>Fees</div>
                                        <div>{ usd(cart.fees || 0) }</div>
                                    </div>) : null }
                                    <div className='cart-tax'>
                                        <div>Taxes</div>
                                        <div>{ usd(cart.tax) }</div>
                                    </div>
                                    <div className='cart-shipping'>
                                        <div>Shipping &amp; Handling</div>
                                        <div>{ usd(cart.shipping) }</div>
                                    </div>
                                    <div className='cart-total'>
                                        <div>Total</div>
                                        <div>{ usd(cart.total) }</div>
                                    </div>
                                </div>
                            </div>
                            { onCancelEvent && (
                                <div className="align-middle">
                                    <Button
                                        variant='outlined'
                                        size='small'
                                        className='corp'
                                        onClick={onCancelEvent}
                                        disabled={state.loading}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            ) }
                        </div>
                    </div>) }
                    { !orderState.ready && <Box className='shadow p-3 bg-white rounded border' sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 100 }}><CircularProgress /></Box> }
                </div>
                <div>
                    { cart.total > 0 && (
                    <Stack direction='row' className='mt-2' spacing={2} justifyContent='flex-start' alignItems='center'>
                        <Stack direction='row' spacing={1} alignItems='center' justifyContent='center'>
                            <IconButton title='Credit Card Payment' color={paymentMethod === 'cc' ? 'primary' : 'default'} onClick={() => {
                                setPaymentMethod('cc');
                                refresh();
                            }} disabled={state.loading}>
                                <CreditCardIcon />
                            </IconButton>
                            <Typography color={paymentMethod === 'cc' ? 'primary' : 'default'} variant='caption' className='ml-1'>Credit Card</Typography>
                        </Stack>
                        {paymentMethods?.includes('crypto') && (
                        <Stack direction='row' spacing={1} alignItems='center' justifyContent='center'>
                            <IconButton color={paymentMethod === 'crypto' ? 'primary' : 'default'} onClick={() => {
                                setPaymentMethod('crypto');
                                refresh();
                            }} disabled={state.loading}>
                                <AccountBalanceWalletIcon />
                            </IconButton>
                            <Typography color={paymentMethod === 'crypto' ? 'primary' : 'default'} variant='caption' className='ml-1'>Digital Payment</Typography>
                        </Stack>)}
                    </Stack>)}
                    <h4 className='main-header mb-4 uppercase'>{paymentMethod === 'cc'? 'Credit Card Payment' : 'Digital Payment'}</h4>
                    <div className='rel'>
                        { state.loading && <div className="rounded full-cover align-middle bkg-semitrans"><BootStrapSpinner /></div> }

                        { !state.loading && cart.total > 0 && (
                        <div className={`shadow p-3 ${orderState.ready ? 'pb-5' : ''} bg-white rounded border ${state.loading? 'disabled' : ''}`}>
                            <div hidden={paymentMethod !== 'cc' || !orderState.ready}>
                                <HeartlandIFrameFormComponent
                                    data={ data }
                                    onChangeEvent={ onChangeEvent }
                                    errors={ errors }
                                    regions={ regions }
                                    setData={ setData }
                                    msg={ msg }
                                />
                            </div>
                            
                            { paymentMethod !== 'cc' && orderState.ready && (
                                <CryptoCheckout
                                    data={ {...data, order, cart} }
                                    amount={ cart.total.toString() }
                                    setData={ setData }
                                    setOrderSuccess={ setOrderSuccess }
                                />
                            )}
                            { !orderState.ready && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                    <CircularProgress />
                                </Box>) }
                        </div>)
                        }

                        { !state.loading && cart.total <= 0 && (
                    
                            <div className="shadow p-3 pb-5 bg-white rounded border" hidden={paymentMethod !== 'cc' || !orderState.ready }>
                                <UploadCsvAlert errorClass={ `info` } errorMessage={ `It looks like you have an order that is free!` } />
                                <HeartlandIFrameFormComponent
                                    id="free-payment-form"
                                    data={ data }
                                    onChangeEvent={ onChangeEvent }
                                    errors={ errors }
                                    regions={ regions }
                                    setData={ setData }
                                    msg={ msg }
                                    useCreditCard={ false }
                                />
                                <div className="align-middle mt-4">
                                    <Button variant={buttonState?.loading? 'text' : 'outlined'} size='small' className={buttonState?.loading? 'no-appearance' : 'corp'} onClick={ onFreeProductSubmit } disabled={ buttonState?.loading }>{buttonState.loading? <CircularProgress /> : 'Complete Order'}</Button>
                                </div>
                            </div>
                    )
                        }
                    </div>
                </div>
            </div>
        </ViewMainWindow>
    )
}

export const HeartlandIFrameFormComponent = (
    { children, data, onChangeEvent, errors, regions, setData, msg, onSubmitEvent, useCreditCard = true, id = 'payment-form' } :
    { children?: JSX.Element | ReactNode, id?: string, data: IAnyType<any>, onChangeEvent: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<any>, f?: (v: string) => string) => void, errors: any[], regions: IComponentState<any>, setData: (data: any) => void, onSubmitEvent?: (e: React.FormEvent<HTMLFormElement>) => void, msg: {msg: string, type: boolean }, useCreditCard?: boolean }) => {

    return (
    <form id={ id } onSubmit={(e) => { if(typeof onSubmitEvent === "function") onSubmitEvent(e); } } method='post'>
        <div className="col-count-">
            <div>
                <InputLabel size='small' className='uppercase bold required'>Address 1</InputLabel>
                <TextField
                    className='mt-0 pt-0'
                    variant='outlined'
                    fullWidth
                    required
                    name='address_1'
                    value={data.address_1 || ''}
                    onChange={onChangeEvent}
                />
            </div>
            <div>
                <InputLabel size='small' className='uppercase bold'>Billing Suite / Box #</InputLabel>
                <TextField
                    className='mt-0 pt-0'
                    variant='outlined'
                    fullWidth
                    name='address_2'
                    value={data.address_2 || ''}
                    onChange={onChangeEvent}
                />
            </div>
            <div>
                <InputLabel size='small' className='uppercase bold required'>Billing City</InputLabel>
                <TextField
                    className='mt-0 pt-0'
                    variant='outlined'
                    fullWidth
                    name='city'
                    value={ data.city || '' }
                    onChange={ onChangeEvent }
                    error={errors.includes('city')}
                    helperText={errors.includes('city')? "Your city is required" : ''}
                />
            </div>
            <div>
                <InputLabel size='small' className='uppercase bold'>Billing Country</InputLabel>
                <Select
                    fullWidth
                    variant='outlined'
                    name='country'
                    value={data.country || 'US'}
                    onChange={e => onChangeEvent(e)}
                    required
                >
                    <MenuItem value=''>Select</MenuItem>
                    <MenuItem value='US'>United States</MenuItem>
                </Select>
            </div>
            <div>
                <InputLabel size='small' className='uppercase bold required'>Billing State</InputLabel>
                { regions.ready? 
                <Select
                    fullWidth
                    name='region'
                    value={data.region || ''}
                    onChange={ e => onChangeEvent(e) }
                    required
                    error={errors.includes('region')}
                >
                    {
                        regions.data && regions.data.map((r: any, k: number) => (
                            <MenuItem key={`region-${k}`} value={ r.value }>{ r.label }</MenuItem>
                        ))
                    }
                </Select> : <BootStrapSpinner /> }
            </div>
            <div>
                <InputLabel size='small' className='uppercase bold required'>Billing Post Code</InputLabel>
                <TextField
                    className='mt-0 pt-0'
                    variant='outlined'
                    fullWidth
                    name='postcode'
                    value={ data.postcode || '' }
                    onChange={e => onChangeEvent(e, (value: string) => value.replace(/[^\d]/gi, '').substring(0, 5))}
                    error={errors.includes('postcode')}
                    helperText={errors.includes('postcode')? "A post code is required" : ''}
                />
            </div>

            <div className='mb-4' hidden>
                <InputLabel size='small' className='uppercase bold'>Store payment for later?</InputLabel>
                <FormControlLabel
                    name='store_card'
                    value={data.store_card || false}
                    control={ <Switch /> }
                    label="Yes, save for later."
                    onChange={(e: any) => setData({...data, store_card: e.target?.checked || false})}
                />
            </div>

        </div>
        { useCreditCard && <div id="credit-card"></div> }
        { msg.msg !== '' && <UploadCsvAlert errorClass={ msg.type? 'success' : 'danger' } errorMessage={ msg.msg } /> }
        { children || null}
    </form>)
}

interface ICryptoCheckout {
    amount: string,
    paylinkId?: string,
    network?: NetworkType | undefined,
    data: any,
    setOrderSuccess: (data: any) => void
    setData?: (data: any) => void
}

interface IHelioSuccessResponse {
    transaction: string,
    data: {
        content: Record<string, unknown>,
        transactionSignature: string,
        status: 'SUCCESS' | 'PENDING' | 'FAILED',
        statusToken: string
    },
    blockchainSymbol: string,
    senderPK: string
}

export const CryptoCheckout = ({
    amount,
    paylinkId,
    network,
    data,
    setOrderSuccess
}: ICryptoCheckout) => {
    const [ready, setReady] = useState<boolean>(false);
    const [config, setConfig] = useState<HelioEmbedConfig | null>(null);
    const [processing, setProcessing] = useState<boolean>(false);
    const [ systemSettings, setSystemSettings ] = useState<ISettingsUser | undefined>(undefined);
    const { account } = useContext(AccountContext);
    const nav = (path: string) => window.location.href = path;


    useEffect(() => {
        if(!systemSettings) {
            GeneralAccountSettingsGetService(account.ID || '').then(r => {
                if(r.success) {
                    setSystemSettings(r.data);
                }
            });
        }
    }, [systemSettings]);

    useEffect(() => {
        if(!systemSettings) return;

        if(ready) 
            setReady(false);
        const fetchConvertedAmount = async () => {
            const isSol: boolean = systemSettings?.global?.cryptoType === 'SOL';
            const convertedAmount = !isSol ? amount : await convertCurrencyToSOL(amount);
            setConfig({
                paylinkId: systemSettings?.global?.cryptoPaymentId || environment.helioPaylink || paylinkId,
                theme: {"themeMode":"light"},
                amount: amount,
                network: network || environment.helioNetwork as NetworkType,
                display: "inline",
                onSuccess: async (event: unknown) => {
                    const response = event as IHelioSuccessResponse;
                    const payResponse = await ShoppingCartPaymentService({
                        ...data,
                        payment_type: 'crypto',
                        base_amount: amount,
                        converted_amount: convertedAmount,
                        chain_symbol: response.blockchainSymbol,
                        payment_token: response.data.transactionSignature,
                        payment_id: response.data.statusToken,
                    });
                    setProcessing(false);
                    setOrderSuccess({
                        summary: {
                            order_id: payResponse.data.order_id,
                            payment_id: payResponse.data.payment_id,
                        },
                        ready: true
                    });
                    nav('/');
                },
                onError: (event: any) => {
                    console.log(event);
                    setProcessing(false);
                },
                onPending: (event: any) => {
                    console.log(event);
                    setProcessing(false);
                },
                onCancel: () => setProcessing(false),
                onStartPayment: () => setProcessing(true),
            });
            setReady(true);
        };
        fetchConvertedAmount();
    }, [amount, isChromeCompatible, systemSettings]);
    
  return (
    ready && config && (
    <Stack
        display="flex"
        flexDirection="row"
        justifyContent="center"
        alignItems="center"
        className={processing ? 'disabled-block disabled-color disabled-image' : ''}
    >
    { !isChromeCompatible() && (
        <AlertBox
            errorClass="error"
            errorMessage="This payment method is not supported on your browser. A web3-compatible browser with the is required to use Helio Checkout."
        />
    )}
    { isChromeCompatible() && <HelioCheckout config={config as HelioEmbedConfig} /> }
    { processing && (
        <Stack
            display="flex"
            justifyContent="center"
            alignItems="center"
            sx={{ top: 0, left: 0, right: 0, bottom: 0, position: 'absolute', zIndex: 1 }}
        >
            <BootStrapSpinner />
        </Stack>
    )}
    </Stack>)
);
}

const isChromeCompatible = () => {
  const ua = navigator.userAgent;
  // Checks for Chrome, but excludes Edge and Opera (which also use Chromium)
  return /Chrome/.test(ua) && !/Edg|OPR|Brave\//.test(ua);
}

const convertCurrencyToSOL = async (amount: string | number): Promise<string> => {
    const response = await fetch('https://api.coingecko.com/api/v3/simple/price?ids=solana&vs_currencies=usd');
    const r = await response.json();
    const solanaUsd = r.solana.usd;
    return ((typeof amount === 'string' ? parseFloat(amount) : amount) / solanaUsd).toString();
}
